import { createContext, ProviderProps, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { env, logToDev } from 'utils';
// import type removed since we're not using Okta types anymore

/** The common interface shared by all versions of the auth context. */
interface BaseAuthContext {
	loginCallback: () => Promise<void>;
	login: (username: string, password: string, redirect: string | undefined, twoFactorCode?: string) => Promise<void>;
	logout: () => Promise<void>;
	clearError: () => void;
	creds: { email: string; password: string };
	setCreds: React.Dispatch<React.SetStateAction<{ email: string; password: string }>>;
}

/** The auth context when we haven't yet checked for existing user information. */
export interface InitializedAuthContext extends BaseAuthContext {
	status: 'initialized';
	data: undefined;
}

/** The auth context when the current user is logged out. */
export interface UnauthenticatedAuthContext extends BaseAuthContext {
	status: 'unauthenticated';
	data: undefined;
}

/** The auth context when we are logging the user in. */
export interface AuthenticatingAuthContext extends BaseAuthContext {
	status: 'authenticating';
	data: undefined;
}

export interface RequiresAuthAuthContext extends BaseAuthContext {
	status: 'requires_2fa';
	data: undefined;
}

/** The auth context when the user is logged in. */
export interface AuthenticatedAuthContext extends BaseAuthContext {
	status: 'authenticated';
	data: {
		user: UserClaims;
	};
}

/** The auth context when the API has returned an error. */
export interface ApiErrorAuthContext extends BaseAuthContext {
	status: 'api_error';
	data: {
		error: AuthApiError;
	};
}

/** The auth context when an unknown error has occurred. */
export interface UnknownErrorAuthContext extends BaseAuthContext {
	status: 'unknown_error';
	data: {
		error: unknown;
	};
}

export type AnyAuthContext =
	| InitializedAuthContext
	| UnauthenticatedAuthContext
	| AuthenticatingAuthContext
	| AuthenticatedAuthContext
	| ApiErrorAuthContext
	| UnknownErrorAuthContext
	| RequiresAuthAuthContext;

type AuthProviderProps = Omit<ProviderProps<AnyAuthContext>, 'value'>;

// Custom types to replace Okta-specific ones
type UserClaims = {
	sub: string;
	name?: string;
	email?: string;
	given_name: string;
	family_name: string;
};

interface AuthApiError {
	errorCode: string;
	message?: string;
}

// API endpoints configuration
const AUTH_API_BASE = process.env.REACT_APP_API_BASE;
const ENDPOINTS = {
	login: `${AUTH_API_BASE}/settings/login`,
	logout: `${AUTH_API_BASE}/settings/logout`,
	userInfo: `${AUTH_API_BASE}/settings/userinfo`,
	csrfToken: `${AUTH_API_BASE}/settings/csrf_token`,
};

// Initialize the authentication context
const authContext = createContext<AnyAuthContext>({} as AnyAuthContext);

// Helper function to get cookie value
export const getCookie = (name: string): string | null => {
	if (typeof document === 'undefined') return null; // SSR safety
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);
	if (parts.length === 2) return decodeURIComponent(parts.pop()!.split(';').shift()!);
	return null;
};

// Create a module-level variable to hold the CSRF token
export let currentCsrfToken: string | null = null;

// Export a function to set the CSRF token
export const setCsrfTokenGlobal = (token: string | null) => {
	currentCsrfToken = token;
};

export const getCsrfTokenGlobal = () => {
	return currentCsrfToken;
};

export function AuthProvider({ children }: AuthProviderProps) {
	const [status, setStatus] = useState<AnyAuthContext['status']>('initialized');
	const [user, setUser] = useState<UserClaims | undefined>();
	const [error, setError] = useState<AuthApiError | unknown | undefined>();
	const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
	const [creds, setCreds] = useState({
		email: '',
		password: '',
	});

	// NEW: State for CSRF token
	const [csrfToken, setCsrfToken] = useState<string | null>(null);
	// Ref for current token (avoids re-renders)
	const csrfRef = useRef<string | null>(null);
	// Ref for pending token requests
	const csrfPromiseRef = useRef<Promise<string> | null>(null);

	useEffect(() => {
		if (csrfToken !== null) {
			setCsrfTokenGlobal(csrfToken);
		}
		setCsrfTokenGlobal(csrfToken);
	}, [csrfToken]);

	/**
	 * Ensures CSRF token is available (updated version)
	 */
	const ensureCSRFToken = useCallback(async (): Promise<string> => {
		// Use ref value to avoid state dependency
		if (csrfRef.current) return csrfRef.current;
		// Return pending promise if already fetching
		if (csrfPromiseRef.current) {
			return await csrfPromiseRef.current;
		}
		try {
			const fetchPromise = (async () => {
				logToDev('Fetching CSRF token...');
				const response = await fetch(ENDPOINTS.csrfToken, {
					method: 'GET',
					credentials: 'include', // Include cookies for session
				});
				if (!response.ok) throw new Error('CSRF token fetch failed');
				// PARSE TOKEN FROM RESPONSE BODY
				const data = await response.json();
				if (!data.csrf_token) {
					throw new Error('CSRF token not found in response');
				}
				const token = data.csrf_token;
				// Update state and ref
				setCsrfToken(token);
				csrfRef.current = token;
				return token;
			})();
			// Store promise in ref
			csrfPromiseRef.current = fetchPromise;
			return await fetchPromise;
		} finally {
			// Reset promise ref when done
			csrfPromiseRef.current = null;
		}
	}, []); // No dependencies

	/**
	 * Creates authenticated requests (updated)
	 */
	const createAuthRequest = useCallback(
		async (url: string, method: 'GET' | 'POST', body?: any) => {
			const headers = new Headers({
				'Content-Type': 'application/json',
				Accept: 'application/json',
			});
			// Add CSRF token for non-GET requests
			if (method !== 'GET') {
				try {
					const token = await ensureCSRFToken();
					headers.append('x-csrf-token', token);
				} catch (e) {
					console.error('CSRF token fetch failed:', e);
					throw new Error('Failed to obtain CSRF protection token');
				}
			}
			return fetch(url, {
				method,
				headers,
				credentials: 'include',
				body: JSON.stringify(body),
			});
		},
		[ensureCSRFToken]
	);

	/**
	 * Attempts to log the user in using flask_login.
	 */
	const login = async (username: string, password: string, redirect: string | undefined, twoFactorCode?: string) => {
		try {
			logToDev(`Handling login request...`);
			setStatus('authenticating');

			const requestBody: any = { username, password };
			if (twoFactorCode) {
				requestBody.two_factor_code = twoFactorCode;
			}

			const response = await createAuthRequest(ENDPOINTS.login, 'POST', requestBody);

			// Handle 307 redirect for password reset
			if (response.status === 307) {
				const encodedEmail = encodeURIComponent(username);
				window.location.href = `/reset-password?email=${encodedEmail}&already_sent=1`;
				return; // Exit function after redirect
			}

			// Handle 428 for 2FA requirement
			if (response.status === 428) {
				setRequiresTwoFactor(true);
				setStatus('requires_2fa');
				const errorResponse = await response.json();

				// set error in this just includes helpful directions
				setError({ errorCode: errorResponse.code, message: errorResponse.message });

				// we don't consider this a real error and we use it as a redirect to the 2fa input screen
				// throwing an error here is unsafe because mobile DOM updates will skip the setStatus change
				return;
			}

			if (!response.ok) {
				const errorResponse = await response.json();
				throw new ApiError(errorResponse.error, errorResponse.message);
			}

			// Get user info after successful login
			const userData = await createAuthRequest(ENDPOINTS.userInfo, 'GET');
			if (!userData.ok) throw new Error('Failed to fetch user info');
			const userInfo = await userData.json();

			// clear creds state for extra safety
			setCreds({ email: '', password: '' });
			setStatus('authenticated');
			setUser(userInfo);
			setRequiresTwoFactor(false); // Reset 2FA state on success

			// Perform redirect if requested
			if (redirect) {
				window.location.href = redirect;
			}
		} catch (e: unknown) {
			if (e instanceof ApiError) {
				logToDev(`Auth API Error:`, JSON.stringify(e, null, 2));
				setStatus('api_error');
				setError({ errorCode: e.code, message: e.message });
			} else {
				logToDev(`Unknown auth error:`, JSON.stringify(e, null, 2));
				setStatus('unknown_error');
				if (e instanceof Error) {
					// Handle standard error
					setError({ message: e.message });
				} else {
					// Fallback for non-error objects
					setError({ message: 'An unexpected error occurred' });
				}
			}
		}
	};

	/**
	 * Placeholder for login callback (no longer needed with flask_login)
	 */
	const loginCallback: AnyAuthContext['loginCallback'] = async () => {
		// Not used with session-based auth
	};

	/**
	 * Logs the user out by clearing session cookies.
	 */
	const logout: AnyAuthContext['logout'] = async () => {
		try {
			logToDev(`Handling logout request...`);
			setStatus('authenticating');
			const response = await createAuthRequest(ENDPOINTS.logout, 'POST');
			if (!response.ok) throw new Error('Logout failed');
			setStatus('unauthenticated');
			setUser(undefined);
			setRequiresTwoFactor(false); // Reset 2FA state on logout
		} catch (e) {
			logToDev(`Logout error:`, JSON.stringify(e, null, 2));
			setStatus('unknown_error');
			setError(e);
		}
	};

	/**
	 * Clears any existing authentication errors.
	 */
	const clearError: AnyAuthContext['clearError'] = useCallback(() => {
		logToDev(`Clearing error status...`);
		if (!error) return;
		setError(undefined);
		setStatus('unauthenticated');
		setRequiresTwoFactor(false); // Reset 2FA when clearing error
	}, [error]);

	const checkAuthStatus = useCallback(async () => {
		try {
			logToDev(`Checking auth status...`);
			// First, ensure we have a CSRF token before checking user info
			try {
				logToDev('Getting new CSRF token...');
				await ensureCSRFToken();
				logToDev('Got new CSRF token!');
			} catch (e) {
				console.error('Failed to get CSRF token', e);
				throw new Error('CSRF_TOKEN_FETCH_FAILED');
			}
			const response = await createAuthRequest(ENDPOINTS.userInfo, 'GET');
			if (response.ok) {
				const userInfo = await response.json();
				await ensureCSRFToken();
				setStatus('authenticated');
				setUser(userInfo);
			} else {
				logToDev(`Not authenticated`);
				setStatus('unauthenticated');
				setUser(undefined);
			}
		} catch (e) {
			console.error(`Auth status check error:`, e);
			setStatus('unknown_error');
			setError(e);
		}
	}, [createAuthRequest, ensureCSRFToken]);

	// Initialize authentication state
	useEffect(() => {
		checkAuthStatus();
	}, [checkAuthStatus]);

	const values = useMemo(
		() => ({
			status,
			data: { user, error, csrfToken },
			login,
			loginCallback,
			logout,
			creds,
			setCreds,
			clearError,
			requiresTwoFactor,
			setRequiresTwoFactor,
		}),
		[status, user, error, csrfToken, requiresTwoFactor, creds, setCreds]
	) as AnyAuthContext;

	return <authContext.Provider value={values}>{children}</authContext.Provider>;
}

// Helper class for API errors
class ApiError extends Error {
	constructor(public code: string, message?: string) {
		super(message);
		this.name = 'ApiError';
	}
}

// Maintain the same hook interface for consumers
export const useAuth = <T extends AnyAuthContext = AuthenticatedAuthContext>() => useContext(authContext) as T;

export default AuthProvider;
